import { FileTypeEnum } from 'api/documents';
import { Button } from 'components/ui/Button';
import { GenericLoading } from 'components/ui/Loader';
import { Sizes } from 'components/forms/NumberSelector';
import { ErrorMessage } from 'components/forms/fragments/FieldSet';
import { useDocumentViewerDialog } from 'components/domain/Document/useDocumentViewerDialog';
import { CheckIcon, UploadIcon, DocumentIcon } from 'components/icons/Icons';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { NsKey } from 'enums/NsKey';
import { Eye } from 'lucide-react';
import { DragEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BorrowerProjectDocumentDto,
  getBorrowerProjectDocuments,
  uploadBorrowerProjectDocuments
} from 'services/borrowerProjectDocumentsAPI';

interface ProjectDocumentConfig {
  fileType: FileTypeEnum;
  labelKey: string;
}

export const PROJECT_DOCUMENTS: ProjectDocumentConfig[] = [
  { fileType: FileTypeEnum.OffreDePret, labelKey: 'BORROWER.LABELS.LOAN_OFFER' },
  { fileType: FileTypeEnum.TableauAmortissement, labelKey: 'BORROWER.LABELS.AMORTIZATION_SCHEDULE' }
];

const isPdfFile = (file: File) => file.name.toLowerCase().endsWith('.pdf');

interface ProjectDocumentItemProps {
  borrowerProjectId: string;
  label: string;
  fileType: FileTypeEnum;
  documents: BorrowerProjectDocumentDto[];
  onChanged: () => Promise<void>;
}

/**
 * Bloc d'upload d'un lot de documents du projet (plusieurs fichiers possibles).
 * Chaque envoi REMPLACE l'ensemble des fichiers existants du même type.
 */
const ProjectDocumentItem: FC<ProjectDocumentItemProps> = ({
  borrowerProjectId,
  label,
  fileType,
  documents,
  onChanged
}) => {
  const { t } = useTranslation(NsKey.Borrower)
  const { openDocumentViewer } = useDocumentViewerDialog()
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string>()
  const [isDragOver, setIsDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasDocuments = documents.length > 0

  const handleUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) {
      return
    }
    setIsUploading(true)
    setError(undefined)
    try {
      const response = await uploadBorrowerProjectDocuments(borrowerProjectId, fileType, files)
      if (response.isSuccess) {
        await onChanged()
      } else {
        setError(response.errorMessage || t('BORROWER.LABELS.DOCUMENT_UPLOAD_ERROR'))
      }
    } catch (uploadError) {
      console.error('Error uploading project documents:', uploadError)
      setError(t('BORROWER.LABELS.DOCUMENT_UPLOAD_ERROR'))
    } finally {
      setIsUploading(false)
    }
  }, [borrowerProjectId, fileType, onChanged, t])

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    await handleUpload(files)
  }, [handleUpload])

  const handleClickUpload = useCallback(() => {
    if (!isUploading) {
      inputRef.current?.click()
    }
  }, [isUploading])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isUploading) {
      setIsDragOver(true)
    }
  }, [isUploading])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    if (isUploading) {
      return
    }
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length === 0) {
      return
    }
    if (!files.every(isPdfFile)) {
      setError('Format non supporté. Formats acceptés: .pdf')
      return
    }
    await handleUpload(files)
  }, [isUploading, handleUpload])

  return (
    <div
      className={`
        border rounded-lg p-4 transition-all duration-200
        ${isDragOver
          ? 'border-primary border-dashed bg-primary/5 border-2'
          : 'border-gray-200 bg-white'
        }
        ${!hasDocuments && !isUploading ? 'cursor-pointer' : ''}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={!hasDocuments && !isUploading ? handleClickUpload : undefined}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        accept=".pdf"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DocumentIcon className="w-5 h-5 text-gray-500" />
          <div>
            <p className="font-medium text-sm">{label}</p>
            {!hasDocuments && (
              <p className="text-xs text-gray-400 mt-0.5">
                {isDragOver ? 'Déposez les fichiers ici' : t('BORROWER.LABELS.DOCUMENTS_UPLOAD_HINT')}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
          {isUploading ? (
            <GenericLoading size={Sizes.sm} isInline loading />
          ) : hasDocuments ? (
            <>
              <div className="flex items-center gap-1.5 text-green-600">
                <CheckIcon className="w-4 h-4" />
                <span className="text-xs">
                  {t('BORROWER.LABELS.DOCUMENTS_UPLOADED_COUNT', { count: documents.length })}
                </span>
              </div>
              <Button
                type="button"
                plain
                onClick={handleClickUpload}
                title={t('BORROWER.LABELS.REPLACE_DOCUMENTS_TOOLTIP')}
              >
                <UploadIcon className="w-4 h-4 text-gray-500 hover:text-primary" />
                <span className="text-xs text-gray-500">{t('BORROWER.LABELS.REPLACE_DOCUMENTS')}</span>
              </Button>
            </>
          ) : (
            <Button color="primary" className="cursor-pointer" onClick={handleClickUpload}>
              <UploadIcon className="w-4 h-4 mr-1" />
            </Button>
          )}
        </div>
      </div>

      {hasDocuments && (
        <ul className="mt-2 flex flex-col gap-1">
          {documents.map((doc) => (
            <li key={doc.storedFileId ?? doc.fileName} className="flex items-center gap-2 text-xs text-gray-500">
              <DocumentIcon className="w-3.5 h-3.5" />
              <span className="truncate">{doc.fileName ?? label}</span>
              {doc.storedFileId && (
                <Button
                  type="button"
                  plain
                  onClick={() => openDocumentViewer({
                    title: doc.fileName ?? label,
                    storedFileId: doc.storedFileId as string,
                    showDefaultActions: true
                  })}
                  title={t('BORROWER.LABELS.VIEW_DOCUMENT')}
                >
                  <Eye className="w-3.5 h-3.5 text-gray-400 hover:text-primary" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  )
}

interface BorrowerProjectDocumentsSectionProps {
  borrowerProjectId: string;
}

/**
 * Section « Documents du projet » : offre de prêt et tableau d'amortissement,
 * facultatifs, rattachés au projet d'emprunt (indépendants du devis). Uploadés
 * par lot : chaque envoi remplace les fichiers existants du même type.
 * Réutilisée dans le tunnel (profiling) et la contractualisation.
 */
export const BorrowerProjectDocumentsSection: FC<BorrowerProjectDocumentsSectionProps> = ({ borrowerProjectId }) => {
  const { t } = useTranslation(NsKey.Borrower)
  const [documents, setDocuments] = useState<BorrowerProjectDocumentDto[]>([])

  const refreshDocuments = useCallback(async () => {
    if (!borrowerProjectId) {
      return
    }
    try {
      const response = await getBorrowerProjectDocuments(borrowerProjectId)
      setDocuments(response?.documents ?? [])
    } catch (error) {
      console.error('Error fetching project documents:', error)
    }
  }, [borrowerProjectId])

  useEffect(() => {
    refreshDocuments()
  }, [refreshDocuments])

  if (!borrowerProjectId) {
    return null
  }

  const DocumentSectionIcon = DocumentIcon

  return (
    <CardsSection
      icon={<DocumentSectionIcon className="h-8 w-8 text-primary" />}
      title={t('BORROWER.LABELS.PROJECT_DOCUMENTS')}
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm text-gray-500">{t('BORROWER.LABELS.PROJECT_DOCUMENTS_OPTIONAL')}</p>
        <p className="text-sm text-gray-500">{t('BORROWER.LABELS.PROJECT_DOCUMENTS_MULTI_FILES_HINT')}</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {PROJECT_DOCUMENTS.map(({ fileType, labelKey }) => (
            <ProjectDocumentItem
              key={fileType}
              borrowerProjectId={borrowerProjectId}
              label={t(labelKey)}
              fileType={fileType}
              documents={documents.filter((d) => d.fileType === fileType)}
              onChanged={refreshDocuments}
            />
          ))}
        </div>
      </div>
    </CardsSection>
  )
}
